import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { Bill, Topic, UserPreferences } from '../types/types';
import { SortOption } from '../components/layout/SortDropdown';
import { homepageFeedSnapshot } from '../data/homepageFeed';

interface BillContextType {
  bills: Bill[];
  loading: boolean;
  error: string | null;
  userPreferences: UserPreferences;
  sortBy: SortOption;
  refreshBills: () => Promise<void>;
  filterBillsByTopic: (topic: Topic) => Bill[];
  trackBill: (billId: string) => void;
  untrackBill: (billId: string) => void;
  setUserPreferences: (prefs: Partial<UserPreferences>) => void;
  setSortBy: (sort: SortOption) => void;
  getSortedBills: () => Bill[];
  loadingProgress: number;
  connectionStatus: 'unknown' | 'connected' | 'disconnected' | 'error';
  userRole: 'user' | 'admin' | null; // New: User role
}

const defaultPreferences: UserPreferences = {
  trackedBills: [],
  topics: [],
  emailNotifications: false,
  preferredCommittees: [],
  preferredStages: []
};

// Load preferences from localStorage with better error handling
const loadPreferences = (): UserPreferences => {
  try {
    const saved = localStorage.getItem('userPreferences');
    if (saved) {
      const parsed = JSON.parse(saved);
      console.log('Loaded preferences from localStorage:', parsed);
      
      // Ensure the structure is correct
      return {
        trackedBills: Array.isArray(parsed.trackedBills) ? parsed.trackedBills : [],
        topics: Array.isArray(parsed.topics) ? parsed.topics : [],
        emailNotifications: Boolean(parsed.emailNotifications),
        preferredCommittees: Array.isArray(parsed.preferredCommittees) ? parsed.preferredCommittees : [],
        preferredStages: Array.isArray(parsed.preferredStages) ? parsed.preferredStages : []
      };
    }
  } catch (error) {
    console.error('Error loading preferences:', error);
  }
  console.log('Using default preferences');
  return defaultPreferences;
};

// Save preferences to localStorage with validation
const savePreferences = (prefs: UserPreferences): void => {
  try {
    const toSave = {
      trackedBills: Array.isArray(prefs.trackedBills) ? prefs.trackedBills : [],
      topics: Array.isArray(prefs.topics) ? prefs.topics : [],
      emailNotifications: Boolean(prefs.emailNotifications),
      preferredCommittees: Array.isArray(prefs.preferredCommittees) ? prefs.preferredCommittees : [],
      preferredStages: Array.isArray(prefs.preferredStages) ? prefs.preferredStages : []
    };
    
    localStorage.setItem('userPreferences', JSON.stringify(toSave));
    console.log('Saved preferences to localStorage:', toSave);
  } catch (error) {
    console.error('Error saving preferences:', error);
  }
};

// Load sort preference from localStorage
const loadSortPreference = (): SortOption => {
  try {
    const saved = localStorage.getItem('billSortPreference');
    if (saved && ['introduced_date_desc', 'last_action_desc', 'title_asc', 'number_asc', 'sponsors_desc'].includes(saved)) {
      return saved as SortOption;
    }
  } catch (error) {
    console.error('Error loading sort preference:', error);
  }
  return 'introduced_date_desc'; // Default sort
};

const BillContext = createContext<BillContextType>({
  bills: [],
  loading: false,
  error: null,
  userPreferences: defaultPreferences,
  sortBy: 'introduced_date_desc',
  refreshBills: async () => {},
  filterBillsByTopic: () => [],
  trackBill: () => {},
  untrackBill: () => {},
  setUserPreferences: () => {},
  setSortBy: () => {},
  getSortedBills: () => [],
  loadingProgress: 0,
  connectionStatus: 'unknown',
  userRole: null, // Default user role
});

export const BillProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [bills, setBills] = useState<Bill[]>(homepageFeedSnapshot);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [userPreferences, setUserPrefs] = useState<UserPreferences>(loadPreferences);
  const [sortBy, setSortByState] = useState<SortOption>(loadSortPreference);
  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'connected' | 'disconnected' | 'error'>('unknown');
  const [userRole, setUserRole] = useState<'user' | 'admin' | null>(null); // New state for user role

  // Fetch user role on auth state change
  useEffect(() => {
    const fetchUserRole = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching user role:', error);
          setUserRole(null);
        } else if (profile) {
          setUserRole(profile.role as 'user' | 'admin');
        } else {
          setUserRole('user'); // Default to user if no profile found
        }
      }
    };

    fetchUserRole();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        fetchUserRole(); // Re-fetch role on auth state change
      } else {
        setUserRole(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const refreshBills = useCallback(async () => {
    try {
      // The bundled snapshot is already visible; database refreshes happen
      // without replacing it with a full-page loading state.
      setLoading(homepageFeedSnapshot.length === 0);
      setLoadingProgress(0);
      setError(null);
      setConnectionStatus('unknown');
      
      const startTime = Date.now();
      setLoadingProgress(20);

      // The homepage reads a tiny precomputed table. Refreshing that cache is
      // database work and never blocks a visitor's first paint.
      const { data, error: fetchError } = await supabase
          .from('homepage_bill_feed')
          .select(`
            rank,
            bill_id,
            number,
            title,
            status,
            chamber,
            introduced_date,
            last_action_date,
            expected_vote_date,
            category,
            congress,
            bill_type,
            bill_number,
            topics,
            sponsor_count,
            refreshed_at
          `)
          .order('rank', { ascending: true });

      if (fetchError) {
        setConnectionStatus('error');
        throw new Error(`Failed to fetch bills: ${fetchError.message}`);
      }

      const allFetchedBills = data || [];
      console.log(`Fetched ${allFetchedBills.length} bills in ${Date.now() - startTime}ms`);
      setLoadingProgress(75);

      const transformedBills: Bill[] = allFetchedBills.map(bill => ({
        id: bill.bill_id,
        number: bill.number,
        title: bill.title,
        summary: null,
        simplifiedText: null,
        originalText: null,
        status: bill.status as Bill['status'],
        chamber: bill.chamber as Bill['chamber'],
        introducedDate: bill.introduced_date,
        lastActionDate: bill.last_action_date || bill.introduced_date,
        expectedVoteDate: bill.expected_vote_date || undefined,
        category: bill.category as Bill['category'],
        congress: bill.congress,
        billType: bill.bill_type,
        billNumber: bill.bill_number,
        topics: Array.isArray(bill.topics) ? bill.topics.filter(Boolean) as Topic[] : [],
        sponsors: [],
        sponsorCount: bill.sponsor_count,
        timeline: [],
        votes: [],
        committees: [],
      }));

      setLoadingProgress(95);
      setBills(transformedBills);
      setLoadingProgress(100);
      setConnectionStatus('connected');
      
      console.log(`Bill transformation completed in ${Date.now() - startTime}ms`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred while fetching bills';
      console.error('Error fetching bills:', {
        error: errorMessage,
        stack: err instanceof Error ? err.stack : undefined,
        type: err instanceof Error ? err.constructor.name : typeof err
      });
      
      setError(errorMessage);
      setConnectionStatus('error');
      
      // Keep the current feed visible if a manual refresh fails.
    } finally {
      setLoading(false);
      // Reset progress after a short delay
      setTimeout(() => setLoadingProgress(0), 500);
    }
  }, []);

  // Memoized sorting function for better performance
  const getSortedBills = useCallback((): Bill[] => {
    const billsCopy = [...bills];
    
    switch (sortBy) {
      case 'introduced_date_desc':
        return billsCopy.sort((a, b) => new Date(b.introducedDate).getTime() - new Date(a.introducedDate).getTime());
      
      case 'last_action_desc':
        return billsCopy.sort((a, b) => new Date(b.lastActionDate).getTime() - new Date(a.lastActionDate).getTime());
      
      case 'title_asc':
        return billsCopy.sort((a, b) => a.title.localeCompare(b.title));
      
      case 'number_asc':
        return billsCopy.sort((a, b) => {
          const aNum = parseInt(a.number.replace(/\D/g, ''), 10) || 0;
          const bNum = parseInt(b.number.replace(/\D/g, ''), 10) || 0;
          return aNum - bNum;
        });
      
      case 'sponsors_desc':
        return billsCopy.sort((a, b) => (b.sponsorCount || b.sponsors?.length || 0) - (a.sponsorCount || a.sponsors?.length || 0));
      
      default:
        return billsCopy;
    }
  }, [bills, sortBy]);

  const filterBillsByTopic = useCallback((topic: Topic) => {
    return getSortedBills().filter(bill => 
      bill.topics?.some(t => 
        t && t.toLowerCase() === topic.toLowerCase()
      )
    );
  }, [getSortedBills]);

  const trackBill = useCallback((billId: string) => {
    console.log('🔖 Tracking bill:', billId);
    
    setUserPrefs(prev => {
      // Ensure we don't add duplicates
      const currentTracked = Array.isArray(prev.trackedBills) ? prev.trackedBills : [];
      
      if (currentTracked.includes(billId)) {
        console.log('Bill already tracked:', billId);
        return prev;
      }
      
      const newPrefs = {
        ...prev,
        trackedBills: [...currentTracked, billId]
      };
      
      console.log('New tracked bills:', newPrefs.trackedBills);
      savePreferences(newPrefs);
      return newPrefs;
    });
  }, []);

  const untrackBill = useCallback((billId: string) => {
    console.log('🗑️ Untracking bill:', billId);
    
    setUserPrefs(prev => {
      const currentTracked = Array.isArray(prev.trackedBills) ? prev.trackedBills : [];
      const newPrefs = {
        ...prev,
        trackedBills: currentTracked.filter(id => id !== billId)
      };
      
      console.log('Remaining tracked bills:', newPrefs.trackedBills);
      savePreferences(newPrefs);
      return newPrefs;
    });
  }, []);

  const setUserPreferences = useCallback((prefs: Partial<UserPreferences>) => {
    console.log('📝 Updating user preferences:', prefs);
    
    setUserPrefs(prev => {
      const newPrefs = { ...prev, ...prefs };
      savePreferences(newPrefs);
      return newPrefs;
    });
  }, []);

  const setSortBy = useCallback((sort: SortOption) => {
    setSortByState(sort);
    localStorage.setItem('billSortPreference', sort);
  }, []);

  // Load preferences on mount and watch for changes
  useEffect(() => {
    const savedPrefs = loadPreferences();
    console.log('Loading preferences on mount:', savedPrefs);
    setUserPrefs(savedPrefs);
  }, []);

  // Debug: Log tracked bills whenever they change
  useEffect(() => {
    console.log('👀 Current tracked bills:', userPreferences.trackedBills);
  }, [userPreferences.trackedBills]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    bills,
    loading,
    error,
    userPreferences,
    sortBy,
    refreshBills,
    filterBillsByTopic,
    trackBill,
    untrackBill,
    setUserPreferences,
    setSortBy,
    getSortedBills,
    loadingProgress,
    connectionStatus,
    userRole
  }), [
    bills,
    loading,
    error,
    userPreferences,
    sortBy,
    refreshBills,
    filterBillsByTopic,
    trackBill,
    untrackBill,
    setUserPreferences,
    setSortBy,
    getSortedBills,
    loadingProgress,
    connectionStatus,
    userRole
  ]);

  return (
    <BillContext.Provider value={contextValue}>
      {children}
    </BillContext.Provider>
  );
};

export const useBillContext = () => {
  const context = useContext(BillContext);
  if (context === undefined) {
    throw new Error('useBillContext must be used within a BillProvider');
  }
  return context;
};
